import { DateTime } from "luxon";
import { useMemo } from "react";
import { useRegisterViewContext } from "@/domains/ai/ai-chat/hooks/use-register-view-context";
import type { AppointmentListFragment } from "@/gql/graphql";
import { timezoneUtils } from "@/lib/timezone";

interface UseAppointmentViewContextProps {
  key: string;
  viewType: string;
  viewIcon?: string;
  currentDate: DateTime;
  startDate?: DateTime;
  endDate?: DateTime;
  appointments: AppointmentListFragment[];
  statusFilters?: string[];
  workerFilters?: string[];
}

export function useAppointmentViewContext({
  key,
  viewType,
  viewIcon = "📅",
  currentDate,
  startDate,
  endDate,
  appointments,
  statusFilters = [],
  workerFilters = [],
}: UseAppointmentViewContextProps) {
  const shortContext = useMemo(() => {
    const viewTypeLabel = viewType.charAt(0).toUpperCase() + viewType.slice(1);
    const dateStr = currentDate.toFormat("MMM dd, yyyy");
    const filterStr =
      statusFilters.length > 0
        ? statusFilters.join(", ")
        : workerFilters.length > 0
          ? "filtered workers"
          : "all";

    return `${viewIcon} ${viewTypeLabel} View: ${dateStr} | ${appointments.length} appointments | Filters: ${filterStr}`;
  }, [viewType, viewIcon, currentDate, appointments.length, statusFilters, workerFilters]);

  const longContext = useMemo(() => {
    if (appointments.length === 0) {
      return `No appointments scheduled for ${currentDate.toFormat("DDDD")}`;
    }

    const maxAppointments = 20;
    const list = appointments
      .slice(0, maxAppointments)
      .map((appointment, i) => {
        const startDt = timezoneUtils.fromISO(appointment.startTime);
        const startTime =
          startDate && endDate && startDate.hasSame(endDate, "day")
            ? startDt.toFormat("h:mm a")
            : startDt.toFormat("MMM dd, h:mm a");
        const customerName =
          appointment.customer?.firstName && appointment.customer?.lastName
            ? `${appointment.customer.firstName} ${appointment.customer.lastName}`.trim()
            : "Unknown customer";

        // Get all services
        const serviceNames =
          appointment.appointmentServices?.map((service) => service.serviceName).join(", ") ||
          "No services";

        // Get workers
        const workerNames =
          appointment.appointmentServices
            ?.map((service) => service.worker?.displayName || "Unassigned")
            .join(", ") || "Unassigned";

        const status = appointment.bookingStatus || "unknown";

        return `${i + 1}. ${startTime} - ${customerName} - ${serviceNames} with ${workerNames} (${status})`;
      })
      .join("\n");

    const remaining = appointments.length - maxAppointments;
    const more = remaining > 0 ? `\n... and ${remaining} more appointments` : "";

    const filterInfo =
      statusFilters.length > 0
        ? `Status: ${statusFilters.join(", ")}`
        : workerFilters.length > 0
          ? "Filtered by workers"
          : "All appointments";

    // Build date range string
    const dateRangeStr =
      startDate && endDate
        ? `${startDate.toFormat("MMM dd")} - ${endDate.toFormat("MMM dd, yyyy")}`
        : currentDate.toFormat("DDDD");

    return `
${viewIcon} ${viewType} View
📆 Date: ${dateRangeStr}
🔍 Active Filters: ${filterInfo}
📊 Total Appointments: ${appointments.length}

Appointment List:
${list}${more}
    `.trim();
  }, [
    appointments,
    viewType,
    viewIcon,
    currentDate,
    startDate,
    endDate,
    statusFilters,
    workerFilters,
  ]);

  useRegisterViewContext({
    key,
    shortContext,
    longContext,
  });
}
